/**
 * Utility functions for image handling
 */

/**
 * Resizes a profile image to reduce file size
 * @param {File} imageFile - The original image file
 * @param {number} maxWidth - Maximum width in pixels (default 800)
 * @param {number} maxHeight - Maximum height in pixels (default 800)
 * @param {number} maxSizeKB - Maximum file size in KB (default 500)
 * @returns {Promise<File>} - A promise that resolves to the resized file
 */
export const resizeProfileImage = (imageFile, maxWidth = 800, maxHeight = 800, maxSizeKB = 500) => {
  return new Promise((resolve, reject) => {
    if (!imageFile || !imageFile.type.startsWith('image/')) {
      reject(new Error('Invalid image file'));
      return;
    }

    // Create a FileReader to read the image
    const reader = new FileReader();
    
    reader.onload = (event) => {
      // Create an image element to get dimensions
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
        
        // Create a canvas to resize the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw the resized image on the canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try different quality settings if needed
        const tryCompress = (quality) => {
          return new Promise((resolveCompress) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolveCompress(null);
                  return;
                }
                resolveCompress(blob);
              },
              imageFile.type,
              quality
            );
          });
        };
        
        // Start with high quality and gradually reduce if needed
        const compressWithQuality = async () => {
          // Try different quality levels until file size is acceptable
          const qualityLevels = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3];
          
          for (let i = 0; i < qualityLevels.length; i++) {
            // Capture the current quality value in a constant to avoid the loop variable issue
            const currentQuality = qualityLevels[i];
            const blob = await tryCompress(currentQuality);
            
            if (!blob) continue;
            
            if (blob.size <= maxSizeKB * 1024 || i === qualityLevels.length - 1) {
              // Create a new File from the blob
              const resizedFile = new File([blob], imageFile.name, {
                type: imageFile.type,
                lastModified: Date.now()
              });
              
              resolve(resizedFile);
              return;
            }
          }
          
          // If we can't compress enough, just return the last attempt
          const finalBlob = await tryCompress(0.3);
          if (finalBlob) {
            const resizedFile = new File([finalBlob], imageFile.name, {
              type: imageFile.type,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        };
        
        compressWithQuality();
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      // Set the image source
      img.src = event.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    // Read the file as a data URL
    reader.readAsDataURL(imageFile);
  });
};
